import { PRIORITY_OPTIONS_MAP } from '@/components/deal/priority/priority-types';
import { Checkbox } from '@/components/ui/checkbox';
import { ComboboxPopover } from '@/components/ui/combobox/combobox-popover';
import { cn, pluralize } from '@/lib/utils';
import type { Priority } from '@/models/types';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useFilteredDealsContext } from '../filtered-deals/filtered-deals-provider';
import type { PriorityFilters } from '../types';

const PriorityFiltersComboBoxPopover = ({
  children,
  comboBoxOnChange,
  comboBoxOpen,
  onOpenChange,
  filters: priorityFilters,
  stayOpenAfterSelection = true,
  alignOffset,
  side = 'bottom',
  align,
  popoverContentClassName,
}: {
  children: ReactNode;
  comboBoxOnChange: (value: string) => void;
  comboBoxOpen: boolean;
  onOpenChange: (open: boolean) => void;
  filters: PriorityFilters;
  stayOpenAfterSelection: boolean;
  alignOffset?: number;
  side?: 'left' | 'top' | 'right' | 'bottom' | undefined;
  align?: 'center' | 'start' | 'end' | undefined;
  popoverContentClassName?: string;
}) => {
  const { getCount } = useFilteredDealsContext();

  const comboBoxOptions = useMemo(() => {
    return PRIORITY_OPTIONS_MAP.map((option) => {
      const count = getCount('priority', option.value as Priority);

      return {
        label: (
          <div className="flex flex-row items-center gap-2 text-nowrap">
            {option.icon}
            {option.label}
            <span className="text-xs text-gray-400">
              {count} {pluralize(count, 'deal', 'deals')}
            </span>
          </div>
        ),
        value: option.value,
        icon: (
          <div className="size-4">
            <Checkbox checked={priorityFilters[option.value as Priority]} />
          </div>
        ),
      };
    });
  }, [priorityFilters, getCount]);

  return (
    <ComboboxPopover
      placeholder={'Status...'}
      options={comboBoxOptions}
      onChange={comboBoxOnChange}
      align={align}
      side={side}
      alignOffset={alignOffset}
      // TODO: Add end adornment and empty message
      endAdornment={undefined}
      emptyMessage={undefined}
      open={comboBoxOpen}
      onOpenChange={onOpenChange}
      stayOpenAfterSelection={stayOpenAfterSelection}
      popoverContentClassName={cn(
        'w-fit min-w-[200px]',
        popoverContentClassName,
      )}
    >
      {children}
    </ComboboxPopover>
  );
};

export default PriorityFiltersComboBoxPopover;
